import React, { useState, useEffect } from 'react';
import { Chrome, Loader2, Copy, Check, AlertCircle, Trash2 } from 'lucide-react';
import { AccordionItem } from './ui/Accordion';
import { extensionApi } from '../lib/api';

interface ApiKeyInfo {
  device_label: string | null;
  masked_key: string;
  issued_at: string;
  last_used_at: string | null;
  key_ref: string;
}

/**
 * Settings → Chrome Extension panel.
 * Lets the user generate a one-use pairing code, paste into the extension,
 * and manage / revoke active extension devices.
 */
export function ChromeExtensionPanel() {
  const [pairingCode, setPairingCode] = useState<string | null>(null);
  const [pairingExpiresAt, setPairingExpiresAt] = useState<Date | null>(null);
  const [secsLeft, setSecsLeft] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [keys, setKeys] = useState<ApiKeyInfo[]>([]);
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadKeys = async () => {
    setLoadingKeys(true);
    try {
      const r = await extensionApi.listKeys();
      setKeys(r.data?.data || []);
    } catch (_) { /* ignore */ }
    finally { setLoadingKeys(false); }
  };

  useEffect(() => { loadKeys(); }, []);

  // Tick countdown
  useEffect(() => {
    if (!pairingExpiresAt) return;
    const t = setInterval(() => {
      const ms = pairingExpiresAt.getTime() - Date.now();
      if (ms <= 0) {
        setPairingCode(null);
        setPairingExpiresAt(null);
        setSecsLeft(0);
        clearInterval(t);
      } else {
        setSecsLeft(Math.ceil(ms / 1000));
      }
    }, 500);
    return () => clearInterval(t);
  }, [pairingExpiresAt]);

  const handleGenerate = async () => {
    setError(null);
    setGenerating(true);
    try {
      const r = await extensionApi.generatePairingCode();
      const data = r.data?.data;
      setPairingCode(data?.pairing_code || null);
      setPairingExpiresAt(data?.expires_at ? new Date(data.expires_at) : null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to generate code');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    if (!pairingCode) return;
    await navigator.clipboard.writeText(pairingCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleRevoke = async (keyRef: string) => {
    if (!window.confirm('Revoke this device? It will need to pair again to use the extension.')) return;
    try {
      await extensionApi.revokeKey(keyRef);
      await loadKeys();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to revoke key');
    }
  };

  const fmtMmSs = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  return (
    <AccordionItem
      title="Chrome Extension"
      subtitle="Pair the Orbit Chrome extension to look up creative codes from anywhere"
      icon={<Chrome className="w-5 h-5" />}
    >
      <div className="space-y-5">
        <div>
          <h3 className="text-sm font-semibold mb-2">Pair a new device</h3>
          <p className="text-xs text-muted-foreground mb-3">
            Generate a one-use code, then paste it into the Orbit extension's setup screen.
          </p>
          {pairingCode ? (
            <div className="rounded-lg border border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 p-4 space-y-2">
              <div className="flex items-center justify-between gap-3">
                <span className="text-2xl font-mono font-bold tracking-widest text-blue-800 dark:text-blue-200">
                  {pairingCode}
                </span>
                <button
                  onClick={handleCopy}
                  className="p-2 rounded hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
                  title="Copy"
                >
                  {copied ? <Check size={16} className="text-green-600" /> : <Copy size={16} />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                Expires in {fmtMmSs(secsLeft)} — single use only.
              </p>
            </div>
          ) : (
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="px-4 py-2 text-sm font-medium rounded bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white inline-flex items-center gap-2"
            >
              {generating ? <Loader2 size={14} className="animate-spin" /> : <Chrome size={14} />}
              Generate Pairing Code
            </button>
          )}
          {error && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-2 flex items-center gap-1">
              <AlertCircle size={12} /> {error}
            </p>
          )}
        </div>

        <div>
          <h3 className="text-sm font-semibold mb-2">Active devices</h3>
          {loadingKeys ? (
            <p className="text-xs text-muted-foreground">Loading…</p>
          ) : keys.length === 0 ? (
            <p className="text-xs text-muted-foreground">No active devices yet.</p>
          ) : (
            <ul className="space-y-2">
              {keys.map(k => (
                <li key={k.key_ref} className="flex items-center justify-between p-3 border border-border rounded-lg text-xs">
                  <div>
                    <p className="font-medium">{k.device_label || 'Unlabeled device'}</p>
                    <p className="text-muted-foreground">
                      Key: <span className="font-mono">{k.masked_key}</span>
                      {' · '}Issued {new Date(k.issued_at).toLocaleDateString()}
                      {k.last_used_at && ` · Last used ${new Date(k.last_used_at).toLocaleString()}`}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRevoke(k.key_ref)}
                    className="p-2 rounded text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                    title="Revoke"
                  >
                    <Trash2 size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </AccordionItem>
  );
}
